import { Injectable } from '@angular/core'
import { Observable, Subject, interval } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

/**
 * 音声レベル情報
 */
export interface AudioLevelInfo {
  /** 現在の音声レベル (0-100) */
  level: number
  /** 発話中かどうか */
  isSpeaking: boolean
}

/**
 * ストラテジー別の音声分析状態
 */
interface AnalysisState {
  /** AnalyserNode */
  analyserNode: AnalyserNode
  /** MediaStreamAudioSourceNode */
  sourceNode: MediaStreamAudioSourceNode
  /** 音声レベルを出力するSubject */
  levelSubject: Subject<AudioLevelInfo>
  /** 分析を停止するためのSubject */
  stopSubject: Subject<void>
}

/**
 * 共有音声レベル監視サービス
 *
 * 目的: 全ての音声キャプチャストラテジーで共有される周波数分析とレベル計算の単一実装を提供
 *
 * 主要責務:
 * - 単一AudioContextインスタンスの管理と共有
 * - ストラテジーごとの個別AnalyserNode生成
 * - 周波数分析とRMSレベル計算
 * - 発話検出（閾値ベース）
 *
 * 依存関係:
 * - Web Audio API (AudioContext, AnalyserNode)
 * - RxJS (Observable, Subject)
 *
 * 使用例:
 * ```typescript
 * const audioLevel$ = audioLevelMonitorService.startAnalysis('cloud-stt', mediaStream);
 * audioLevel$.subscribe(levelInfo => {
 *   console.log('Level:', levelInfo.level, 'Speaking:', levelInfo.isSpeaking);
 * });
 *
 * // 停止時
 * audioLevelMonitorService.stopAnalysis('cloud-stt');
 * ```
 *
 * @requirements 2.2, 2.4, 9.1
 */
@Injectable({
  providedIn: 'root',
})
export class AudioLevelMonitorService {
  /** 共有AudioContextインスタンス */
  private audioContext: AudioContext | null = null

  /** ストラテジー別の分析状態を管理するMap */
  private analysisStates = new Map<string, AnalysisState>()

  /** 発話検出の閾値 (RMS値) */
  private readonly SPEAKING_THRESHOLD = 10

  /** レベルチェックの間隔（ミリ秒） */
  private readonly LEVEL_CHECK_INTERVAL = 100

  constructor() {}

  /**
   * 指定されたストラテジーの音声分析を開始
   *
   * @param strategyId ストラテジーの識別子
   * @param mediaStream 音声入力のMediaStream
   * @returns 音声レベル情報のObservable
   * @throws Error AudioContextまたはAnalyserNodeの作成に失敗した場合
   */
  startAnalysis(
    strategyId: string,
    mediaStream: MediaStream,
  ): Observable<AudioLevelInfo> {
    // 既に分析中の場合は既存のObservableを返す
    if (this.analysisStates.has(strategyId)) {
      console.warn(
        `[AudioLevelMonitor] Strategy ${strategyId} is already analyzing`,
      )
      return this.analysisStates.get(strategyId)!.levelSubject.asObservable()
    }

    // AudioContextが未作成なら作成
    if (this.audioContext == null) {
      this.audioContext = new AudioContext()
      console.log('[AudioLevelMonitor] AudioContext created')
    }

    // AnalyserNodeとSourceNodeを作成
    const analyserNode = this.audioContext.createAnalyser()
    analyserNode.fftSize = 256
    analyserNode.smoothingTimeConstant = 0.8

    const sourceNode = this.audioContext.createMediaStreamSource(mediaStream)
    sourceNode.connect(analyserNode)

    // 音声レベルを出力するSubject
    const levelSubject = new Subject<AudioLevelInfo>()
    const stopSubject = new Subject<void>()

    // 状態を保存
    this.analysisStates.set(strategyId, {
      analyserNode,
      sourceNode,
      levelSubject,
      stopSubject,
    })

    console.log(
      `[AudioLevelMonitor] Started analysis for strategy: ${strategyId}`,
    )

    // 定期的に音声レベルを計算して出力
    this.startLevelMonitoring(strategyId)

    return levelSubject.asObservable()
  }

  /**
   * 指定されたストラテジーの音声分析を停止
   *
   * @param strategyId ストラテジーの識別子
   */
  stopAnalysis(strategyId: string): void {
    const state = this.analysisStates.get(strategyId)
    if (state == null) {
      console.warn(
        `[AudioLevelMonitor] Strategy ${strategyId} is not analyzing`,
      )
      return
    }

    // リソースをクリーンアップ
    state.stopSubject.next()
    state.stopSubject.complete()
    state.sourceNode.disconnect()
    state.analyserNode.disconnect()
    state.levelSubject.complete()

    this.analysisStates.delete(strategyId)
    console.log(
      `[AudioLevelMonitor] Stopped analysis for strategy: ${strategyId}`,
    )

    // 全てのストラテジーが停止したらAudioContextをクローズ
    if (this.analysisStates.size === 0 && this.audioContext != null) {
      this.audioContext.close()
      this.audioContext = null
      console.log('[AudioLevelMonitor] AudioContext closed')
    }
  }

  /**
   * 音声レベルの監視を開始
   *
   * @param strategyId ストラテジーの識別子
   */
  private startLevelMonitoring(strategyId: string): void {
    const state = this.analysisStates.get(strategyId)
    if (state == null) {
      return
    }

    const { analyserNode, levelSubject, stopSubject } = state
    const dataArray = new Uint8Array(analyserNode.frequencyBinCount)

    // 定期的に音声レベルを計算
    interval(this.LEVEL_CHECK_INTERVAL)
      .pipe(
        takeUntil(stopSubject),
        map(() => {
          // 周波数データを取得
          analyserNode.getByteFrequencyData(dataArray)

          // RMSレベルを計算
          let sum = 0
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i] * dataArray[i]
          }
          const rms = Math.sqrt(sum / dataArray.length)

          // 0-100のスケールに正規化
          const level = Math.min(100, (rms / 255) * 100)

          // 発話検出
          const isSpeaking = rms > this.SPEAKING_THRESHOLD

          return { level, isSpeaking }
        }),
      )
      .subscribe((levelInfo) => {
        levelSubject.next(levelInfo)
      })
  }

  /**
   * アクティブなAudioContextが存在するか確認
   *
   * @returns アクティブなAudioContextが存在する場合true
   */
  hasActiveAudioContext(): boolean {
    return this.audioContext != null
  }

  /**
   * アクティブなAnalyserNodeの数を取得
   *
   * @returns アクティブなAnalyserNodeの数
   */
  getActiveAnalysersCount(): number {
    return this.analysisStates.size
  }
}
